import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowLeft, Star, MapPin, Phone, Mail, Globe, Shield, Calendar } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function OutfitterDetail() {
  const { slug } = useParams();
  const [outfitter, setOutfitter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/outfitters/slug/${slug}`)
      .then((r) => { setOutfitter(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-bone flex items-center justify-center pt-20"><p className="font-body text-slate">Loading outfitter...</p></div>;
  if (!outfitter) return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center pt-20 gap-4">
      <p className="font-body text-slate">Outfitter not found</p>
      <Link to="/outfitters" className="btn-outline-dark">Back to Outfitters</Link>
    </div>
  );

  const expeditions = outfitter.expeditions || [];

  return (
    <>
      <Helmet>
        <title>{`${outfitter.name} | Dispatch Hunts`}</title>
        <meta name="description" content={`${outfitter.name} — ${outfitter.description}. Vetted by Dispatch Hunts.`} />
      </Helmet>

      {/* Hero */}
      <section className="section-dark grain pt-28 pb-12" data-testid="outfitter-hero">
        <div className="container-dh">
          <Link to="/outfitters" className="inline-flex items-center gap-2 text-bone/60 hover:text-brass font-label text-xs tracking-wider uppercase mb-6 transition-colors" data-testid="back-to-outfitters">
            <ArrowLeft className="w-4 h-4" /> Back to Outfitters
          </Link>

          <div className="flex items-start gap-6">
            {outfitter.logo && (
              <img src={outfitter.logo} alt={outfitter.name} className="w-20 h-20 object-cover rounded-full border-2 border-brass/30 flex-shrink-0" />
            )}
            <div>
              <div className="flex items-center gap-3 mb-2">
                {outfitter.verified && (
                  <span className="flex items-center gap-1 text-label text-[10px] tracking-wider uppercase text-brass bg-brass/10 px-2 py-1">
                    <Shield className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-bone mb-2" data-testid="outfitter-title">{outfitter.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-bone/60">
                <span className="flex items-center gap-1 font-body text-sm"><MapPin className="w-4 h-4 text-brass" />{outfitter.location}</span>
                <span className="flex items-center gap-1 font-body text-sm"><Star className="w-4 h-4 text-brass" />{outfitter.average_rating} ({outfitter.review_count} reviews)</span>
                <span className="font-body text-sm">{outfitter.years_experience} years experience</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="section-light py-16" data-testid="outfitter-about">
        <div className="container-dh">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl font-bold text-carbon mb-6">About {outfitter.name}</h2>
              <p className="font-body text-slate leading-relaxed text-lg mb-8">{outfitter.description}</p>

              <h3 className="font-display text-lg font-bold text-carbon mb-4">Specialties</h3>
              <div className="flex flex-wrap gap-2 mb-8">
                {outfitter.specialties?.map((s) => (
                  <span key={s} className="bg-brass/10 text-brass font-label text-[10px] tracking-wider uppercase px-3 py-1.5">{s}</span>
                ))}
              </div>
            </div>

            <div className="bg-chalk border border-carbon/10 p-6 h-fit">
              <h3 className="font-display text-lg font-bold text-carbon mb-4">Contact</h3>
              <div className="space-y-4">
                {outfitter.contact_phone && (
                  <a href={`tel:${outfitter.contact_phone}`} className="flex items-center gap-3 text-slate hover:text-brass transition-colors font-body text-sm">
                    <Phone className="w-4 h-4 text-brass" /> {outfitter.contact_phone}
                  </a>
                )}
                {outfitter.contact_email && (
                  <a href={`mailto:${outfitter.contact_email}`} className="flex items-center gap-3 text-slate hover:text-brass transition-colors font-body text-sm">
                    <Mail className="w-4 h-4 text-brass" /> {outfitter.contact_email}
                  </a>
                )}
                {outfitter.website && (
                  <a href={outfitter.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate hover:text-brass transition-colors font-body text-sm">
                    <Globe className="w-4 h-4 text-brass" /> Website
                  </a>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-carbon/10">
                <Link to="/apply" className="btn-brass w-full justify-center text-center" data-testid="outfitter-apply-btn">
                  Book Through Us <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expeditions */}
      {expeditions.length > 0 && (
        <section className="section-chalk py-16" data-testid="outfitter-expeditions">
          <div className="container-dh">
            <p className="text-label text-brass mb-3">Available Expeditions</p>
            <h2 className="font-display text-2xl font-bold text-carbon mb-8">{expeditions.length} {expeditions.length === 1 ? "expedition" : "expeditions"} through {outfitter.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {expeditions.map((exp) => (
                <div key={exp.id || exp.slug} className="bg-bone border border-carbon/10 p-6">
                  <h3 className="font-display text-lg font-bold text-carbon mb-2">{exp.title}</h3>
                  <p className="font-body text-sm text-slate mb-3 line-clamp-2">{exp.description}</p>
                  <div className="flex items-center gap-4 text-xs text-smoke font-body">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{exp.season_start} – {exp.season_end}</span>
                    <span>{exp.duration_days} days</span>
                    <span className="font-mono text-carbon font-bold">${exp.price?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-dark grain py-16" data-testid="outfitter-cta">
        <div className="container-dh text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-bone mb-4">Interested in {outfitter.name}?</h2>
          <p className="font-body text-bone/50 mb-8">We work directly with {outfitter.name} and can handle every detail of your booking.</p>
          <Link to="/apply" className="btn-brass" data-testid="outfitter-cta-apply">Apply for a Consultation <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </>
  );
}
